import { useState, useEffect } from 'react';
import { Toast, ToastContainer, Button, Dropdown } from 'react-bootstrap';
import { useNotifications } from '../hooks/useNotifications';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';

interface PushNotificationProps {
  position?: 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start';
}

export const PushNotification = ({ position = 'top-end' }: PushNotificationProps) => {
  const { user } = useAuth() // eslint-disable-line @typescript-eslint/no-unused-vars;
  const { notifications, markAsRead } = useNotifications();
  const queryClient = useQueryClient();
  const [currentToast, setCurrentToast] = useState<{
    id: string;
    notification: any;
    show: boolean;
  } | null>(null);
  const [notificationQueue, setNotificationQueue] = useState<any[]>([]);
  const [processedIds, setProcessedIds] = useState<Set<string>>(new Set());

  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data;
    },
    enabled: user?.role === 'ADMIN' || user?.role === 'AGENT'
  });

  // Assign ticket mutation
  const assignTicketMutation = useMutation({
    mutationFn: async ({ ticketId, assigneeId }: { ticketId: string; assigneeId: string }) => {
      const response = await api.patch(`/tickets/${ticketId}`, { assigneeId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    }
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'TICKET_CREATED':
        return 'fas fa-ticket-alt text-primary';
      case 'TICKET_ASSIGNED':
        return 'fas fa-user-check text-success';
      case 'TICKET_STATUS_CHANGED':
        return 'fas fa-sync-alt text-info';
      case 'TICKET_PRIORITY_CHANGED':
        return 'fas fa-exclamation-triangle text-warning';
      case 'TICKET_COMMENT_ADDED':
        return 'fas fa-comment text-secondary';
      case 'SLA_FIRST_RESPONSE_DUE':
      case 'SLA_RESOLUTION_DUE':
        return 'fas fa-clock text-danger';
      case 'SLA_BREACHED':
        return 'fas fa-exclamation-circle text-danger';
      default:
        return 'fas fa-bell text-muted';
    }
  };

  const getNotificationVariant = (type: string) => {
    switch (type) {
      case 'TICKET_CREATED':
        return 'primary';
      case 'TICKET_ASSIGNED':
        return 'success';
      case 'TICKET_STATUS_CHANGED':
        return 'info';
      case 'TICKET_PRIORITY_CHANGED':
        return 'warning';
      case 'TICKET_COMMENT_ADDED':
        return 'secondary';
      case 'SLA_FIRST_RESPONSE_DUE':
      case 'SLA_RESOLUTION_DUE':
        return 'danger';
      case 'SLA_BREACHED':
        return 'danger';
      default:
        return 'light';
    }
  };

  // Build notification queue from unread notifications
  useEffect(() => {
    const unreadNotifications = notifications.filter((n: any) => n.status === 'UNREAD');
    
    // Add new notifications to queue (that haven't been processed yet)
    const newNotifications = unreadNotifications.filter(
      (n: any) => !processedIds.has(n.id)
    );
    
    if (newNotifications.length > 0) {
      setNotificationQueue(prev => [...prev, ...newNotifications]);
      // Mark as processed
      setProcessedIds(prev => {
        const newSet = new Set(prev);
        newNotifications.forEach((n: any) => newSet.add(n.id));
        return newSet;
      });
    }
  }, [notifications, processedIds]);

  // Process queue - show one notification at a time
  useEffect(() => {
    // If no current toast and queue has items, show next
    if (!currentToast && notificationQueue.length > 0) {
      const nextNotification = notificationQueue[0];
      const newToast = {
        id: `toast-${nextNotification.id}`,
        notification: nextNotification,
        show: true
      };
      
      setCurrentToast(newToast);
      
      // Remove from queue
      setNotificationQueue(prev => prev.slice(1));
      
      // Auto-hide after 8 seconds
      setTimeout(() => {
        setCurrentToast(null);
      }, 8000);
    }
  }, [currentToast, notificationQueue]);

  const handleToastClose = (notificationId: string) => {
    // Mark notification as read
    markAsRead(notificationId);
    
    // Remove current toast (will trigger next in queue)
    setCurrentToast(null);
  };

  const handleToastClick = (notification: any) => {
    // Mark as read
    markAsRead(notification.id);
    
    // Navigate to ticket if it exists
    if (notification.ticketId) {
      window.location.href = `/tickets/${notification.ticketId}`;
    }
  };

  const handleAssignTicket = (ticketId: string, assigneeId: string) => {
    assignTicketMutation.mutate({ ticketId, assigneeId });
  };

  return (
    <ToastContainer 
      position={position} 
      className="p-3"
      style={{ zIndex: 9999 }}
    >
      {currentToast && (
        <Toast
          key={currentToast.id}
          show={currentToast.show}
          onClose={() => handleToastClose(currentToast.notification.id)}
          delay={8000}
          autohide
          className="mb-2"
          style={{ minWidth: '350px' }}
        >
          <Toast.Header 
            className={`bg-${getNotificationVariant(currentToast.notification.type)} text-white`}
            closeButton={true}
          >
            <div className="d-flex align-items-center w-100">
              <i className={`${getNotificationIcon(currentToast.notification.type)} me-2`}></i>
              <strong className="me-auto">{currentToast.notification.title}</strong>
              {notificationQueue.length > 0 && (
                <span className="badge bg-danger rounded-pill ms-2">
                  +{notificationQueue.length}
                </span>
              )}
            </div>
          </Toast.Header>
          <Toast.Body>
            <div className="d-flex align-items-start">
              <div className="flex-grow-1">
                <p className="mb-1">{currentToast.notification.message}</p>
                <small className="text-muted">
                  {formatDistanceToNow(new Date(currentToast.notification.createdAt), { addSuffix: true })}
                </small>
                
                {/* Quick assignment for new tickets (Admin only) */}
                {currentToast.notification.type === 'TICKET_CREATED' && 
                 (user?.role === 'ADMIN' || user?.role === 'AGENT') && 
                 currentToast.notification.ticketId && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">Quick assign:</small>
                      <Dropdown>
                        <Dropdown.Toggle 
                          variant="outline-primary" 
                          size="sm"
                          className="py-1 px-2"
                          disabled={assignTicketMutation.isPending}
                        >
                          {assignTicketMutation.isPending ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <>
                              <i className="fas fa-user-plus me-1"></i>
                              Assign
                            </>
                          )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item 
                            onClick={() => handleAssignTicket(currentToast.notification.ticketId, '')}
                          >
                            <i className="fas fa-user-slash me-2 text-muted"></i>
                            Unassigned
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          {usersData?.data?.filter((u: any) => u.role === 'AGENT' || u.role === 'ADMIN').map((agent: any) => (
                            <Dropdown.Item 
                              key={agent.id}
                              onClick={() => handleAssignTicket(currentToast.notification.ticketId, agent.id)}
                            >
                              <i className="fas fa-user me-2 text-primary"></i>
                              {agent.name} ({agent.role})
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                )}
              </div>
              {currentToast.notification.ticket && (
                <div className="ms-2">
                  <span className={`badge bg-${getNotificationVariant(currentToast.notification.type)}`}>
                    {currentToast.notification.ticket.priority}
                  </span>
                </div>
              )}
            </div>
            
            {/* Click to view ticket */}
            <div className="mt-2">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="w-100"
                onClick={() => handleToastClick(currentToast.notification)}
              >
                <i className="fas fa-eye me-1"></i>
                View Ticket
              </Button>
            </div>
          </Toast.Body>
        </Toast>
      )}
    </ToastContainer>
  );
};
